<template>
  <div class="multi-step-form">
    <div class="d-flex justify-content-end align-items-center mt-2 px-3">
      <button class="d-none btn btn-sm border-0 rounded bg-transparent d-flex align-items-center fw-bold text-white">
        <i class='bx bx-menu-alt-left fs-3 icon-white'></i>
      </button>
      <button class="btn btn-sm border-0 rounded bg-transparent d-flex align-items-center fw-bold text-white">
        <i class="bi bi-box-arrow-right fs-4 icon-white me-2"></i>
        <a href="/" class="text-xs text-white text-decoration-none">
          Logout
        </a>
      </button>
    </div>

    <!-- Titles container -->
    <div class="onboarding-timeline-titles d-flex justify-content-center mb-2"
        v-if="safe_form_config.sections && safe_form_config.sections.length > 1">
      <template v-for="(section, index) in safe_form_config.sections" :key="'title-' + index">
        <div class="onboarding-timeline-title-wrapper text-center mx-2">
          <span class="onboarding-timeline-title">{{ section.title }}</span>
        </div>
      </template>
    </div>

    <!-- Timeline container -->
    <div class="onboarding-timeline-wrapper d-flex justify-content-center align-items-center"
        v-if="safe_form_config.sections && safe_form_config.sections.length > 1">

      <template v-for="(section, index) in safe_form_config.sections" :key="'step-' + index">

        <!-- Step circle -->
        <div class="onboarding-timeline-step d-flex flex-column align-items-center">
          <div class="onboarding-timeline-circle"
              :class="{
                completed: index < current_section_index,
                active: index === current_section_index,
                upcoming: index > current_section_index
              }">
          </div>
        </div>

        <!-- Line between steps -->
        <div v-if="index < safe_form_config.sections.length - 1"
            class="onboarding-timeline-line"
            :class="{
              completed: index < current_section_index,
              upcoming: index >= current_section_index
            }">
        </div>

      </template>
    </div>


    <div class="forms-container mt-4">
      <div class="carousel-container" ref="carousel">
        <div v-for="(section, section_index) in safe_form_config.sections" :key="'section-' + section_index"
          class="form-wrapper"
          :class="{ active: section_index === current_section_index }"
          :data-index="section_index">
          <div class="form-card mt-0 shadow-lg" v-if="safe_form_config">
            <div class="form-header fw-bold text-x mt-3 shadow-lg" v-if="safe_form_config.form_title">
              {{ safe_form_config.form_title }}
            </div>

            <template v-if="safe_form_config.sections && safe_form_config.sections.length">
              <div
                class="section-container"  :class="{
                  active: section_index === current_section_index,
                  inactive: section_index !== current_section_index
                }">
                <div class="form-fields-container p-3" v-if="section.fields && section.fields.length">
                  <div class="section-header fw-bold mt-3 text-xs d-flex justify-content-between align-items-center"
                    v-if="section.title">
                    <div class="section-title d-flex align-items-center gap-2 fw-bold text-x">
                      {{ section.title }}
                      <i
                        :class="[
                          section.is_verified ? 'bi bi-check-circle text-success' : 'bi bi-exclamation-circle text-danger',
                          'fs-5 fw-bold'
                        ]">
                      </i>

                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <span class="status-badge text-xxs border-1"
                        :class="section.is_verified ? 'verified' : 'pending'">
                        {{ section.is_verified ? 'Verified' : 'Pending verification' }}
                      </span>
                    </div>
                  </div>
                  <hr class="mt-0 fw-bold border border-dark" v-if="section.title" />

                  <!-- Form Questions -->
                  <template v-for="(field, field_index) in section.fields" :key="'field-' + field_index">
                    <div class="form-group" v-if="field && field.model && safe_form_data && !field.is_dependent">

                      <label :for="field.id" class="fw-medium text-xs text-start d-block mt-3" v-if="field.label">
                        {{ field.label }}
                        <span v-if="field.required" class="text-danger fs-5">*</span>
                      </label>

                      <input v-if="field.type === 'text'" type="text"
                        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                        :class="{'border-danger': validation_errors[field.model]}"
                        :id="field.id || ''" v-model="safe_form_data[field.model]" :placeholder="field.placeholder"
                        :required="field.required" maxlength="100" />

                      <textarea v-else-if="field.type === 'textarea'"
                        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                        :class="{'border-danger': validation_errors[field.model]}"
                        :id="field.id || ''" v-model="safe_form_data[field.model]" :placeholder="field.placeholder"
                        :rows="field.rows || 3" :required="field.required"  maxlength="500"></textarea>

                      <input v-else-if="field.type === 'number'" type="number"
                        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                        :class="{'border-danger': validation_errors[field.model]}"
                        :id="field.id || ''" v-model="safe_form_data[field.model]" :placeholder="field.placeholder"
                        :required="field.required" />

                      <input v-else-if="field.type === 'date'" type="date"
                        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                        :class="{'border-danger': validation_errors[field.model]}"
                        :id="field.id || ''" v-model="safe_form_data[field.model]" :required="field.required" />

                      <div v-else-if="field.type === 'facial_verification'"
                        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg">
                        <div v-if="safe_form_data[field.model] && safe_form_data[field.model].file_name">
                          <div class="d-flex align-items-center gap-2">
                            <span class="text-xs">{{ safe_form_data[field.model].file_name }}</span>


                            <button
                              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                              type="button"
                              @click="preview_local_file(field.model)"
                            >
                              Preview
                            </button>

                            <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
                              type="button" @click="remove_file(field.model)">
                              Remove
                            </button>
                          </div>
                        </div>
                        <div v-else>
                          <FacialBiometric :question="field" v-model="safe_form_data[field.model]" />
                        </div>
                      </div>

                      <div v-else-if="field.type === 'file'">
                        <div v-if="safe_form_data[field.model] && safe_form_data[field.model]">
                          <div class="d-flex align-items-center gap-2">
                            <span class="text-xs">{{ safe_form_data[field.model].file_name }}</span>

                            <button
                              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                              type="button"
                              @click="preview_local_file(field.model)"
                            >
                              Preview
                            </button>
                            <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
                              type="button" @click="remove_file(field.model)">
                              Remove
                            </button>
                          </div>
                        </div>

                        <!-- If no file exists -->
                        <div v-else>
                          <input type="file"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            :id="field.id || ''" @change="handle_file_upload($event, field.section_question_id)"
                            :required="field.required" />

                            <div v-if="file_validation_error[field.id]" class="text-danger text-xxs mt-1">
                              {{file_validation_error[field.id]}}
                            </div>

                          <!-- Show comment if required and no file chosen -->
                          <div v-if="!safe_form_data[field.model]?.file" class="mt-2">
                            <label class="text-xs" :for="field.id + '_file_comment'">
                              Please explain why you cannot upload this file:
                            </label>
                            <textarea :id="field.id + '_file_comment'"
                              class="form-control form-control-lg text-xs bg-light border-1 py-1 rounded-lg"
                              v-model="safe_form_data[field.model + '_file_comment']" rows="2"
                              placeholder="Provide a reason..."></textarea>
                          </div>
                        </div>
                      </div>

                      <div v-else-if="field.type === 'single_select' || field.type === 'radio'" class="radio-group"
                          :class="{'border border-danger':
                          submit_attempted && validation_errors[field.model]}"
                        >
                        <div v-for="(option, opt_index) in field.options || []" :key="'radio-' + opt_index"
                          class="form-check">
                          <input class="form-check-input" type="radio" :name="field.id || ''"
                            :id="`${field.id}-${opt_index}`" v-model="safe_form_data[field.model]"
                            :value="option.value || option.text" :required="field.required"
                            @change="update_dependent_questions_visibility(field, safe_form_data[field.model])" />
                          <label class="form-check-label text-xxs" :for="`${field.id}-${opt_index}`">
                            {{ option.text }}
                          </label>
                        </div>
                      </div>

                      <div v-else-if="field.type === 'multi_select' || field.type === 'checkboxes'" :class="{'border border-danger':
                          submit_attempted && validation_errors[field.model]}">
                        <div v-for="(option, opt_index) in field.options || []" :key="'checkbox-' + opt_index"
                          class="form-check">
                          <input class="form-check-input" type="checkbox" :id="`${field.id}-${opt_index}`"
                            :value="option.value || option.text" v-model="safe_form_data[field.model]"
                            @change="update_dependent_questions_visibility(field, safe_form_data[field.model])" />
                          <label class="form-check-label text-xxs" :for="`${field.id}-${opt_index}`">
                            {{ option.text }}
                          </label>
                        </div>
                      </div>

                      <input v-else type="text"
                        class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                        :class="{'border-danger': validation_errors[field.model]}"
                        :id="field.id || ''" v-model="safe_form_data[field.model]" :placeholder="field.placeholder"
                        :required="field.required"
                        @input="update_dependent_questions_visibility(field, safe_form_data[field.model])" />

                      <div v-if="validation_errors[field.model]" class="text-danger text-xxs mt-1">
                        {{ validation_errors[field.model] }}
                      </div>

                      <!-- Dependent Questions -->
                      <div
                        v-if="field.dependent_questions && field.dependent_questions.length"
                        class="mt-3 ms-4 ps-3 border-start border-3 border-primary"
                      >

                        <div
                          v-for="(dq, dq_index) in field.dependent_questions"
                          :key="'dq-' + dq_index"
                          v-show="dq.is_visible"
                          class="mb-3 p-3 rounded shadow-sm"
                        >

                          <!-- Question Label -->
                          <label
                            :for="dq.model"
                            class="fw-medium text-xs text-start d-block mt-2"
                          >
                            {{ dq.label }}
                            <span v-if="dq.required" class="text-danger fs-5">*</span>
                          </label>

                          <!-- INPUT TYPES (unchanged logic, just moved inside card) -->

                          <input
                            v-if="dq.type === 'text'"
                            type="text"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
                            :id="dq.model"
                            v-model="safe_form_data[dq.model]"
                            :placeholder="dq.label"
                            :required="dq.required && dq.is_visible"
                          />

                          <textarea
                            v-else-if="dq.type === 'textarea'"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
                            :id="dq.model"
                            v-model="safe_form_data[dq.model]"
                            :rows="3"
                            :placeholder="dq.label"
                            :required="dq.required && dq.is_visible"
                          ></textarea>

                          <input
                            v-else-if="dq.type === 'number'"
                            type="number"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
                            :id="dq.model"
                            v-model="safe_form_data[dq.model]"
                            :placeholder="dq.label"
                            :required="dq.required && dq.is_visible"
                          />

                          <input
                            v-else-if="dq.type === 'date'"
                            type="date"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
                            :id="dq.model"
                            v-model="safe_form_data[dq.model]"
                            :required="dq.required && dq.is_visible"
                          />

                          <div v-else-if="dq.type === 'facial_verification'"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg">
                            <div v-if="safe_form_data[dq.model] && safe_form_data[dq.model].file_name">
                              <div class="d-flex align-items-center gap-2">
                                <span class="text-xs">{{ safe_form_data[dq.model].file_name }}</span>

                                <button
                                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                  type="button"
                                  @click="preview_local_file(dq.model)"
                                >
                                  Preview
                                </button>
                                <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
                                  type="button" @click="remove_file(dq.model)">
                                  Remove
                                </button>
                              </div>
                            </div>
                            <div v-else>
                              <FacialBiometric :question="dq" v-model="safe_form_data[dq.model]" />
                            </div>
                          </div>

                          <div v-else-if="dq.type === 'file'">
                            <div v-if="safe_form_data[dq.model] && safe_form_data[dq.model]">
                              <div class="d-flex align-items-center gap-2">
                                <span class="text-xs">{{ safe_form_data[dq.model].file_name }}</span>

                                <button
                                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                                  type="button"
                                  @click="preview_local_file(dq.model)"
                                >
                                  Preview
                                </button>
                                <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-white ms-2"
                                  type="button" @click="remove_file(dq.model)">
                                  Remove
                                </button>
                              </div>
                            </div>

                            <div v-else>
                              <input type="file"
                                class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                                :id="dq.model" @change="handle_file_upload($event, dq.model, true)"
                                :required="dq.required" />
                                <div v-if="file_validation_error[dq.model]" class="text-danger text-xxs mt-1">
                                  {{file_validation_error[dq.model]}}
                                </div>
                              <div v-if="!safe_form_data[dq.model]?.file" class="mt-2">
                                <label class="text-xs" :for="dq.model + '_file_comment'">
                                  Please explain why you cannot upload this file:
                                </label>
                                <textarea :id="dq.model + '_file_comment'"
                                  class="form-control form-control-lg text-xs bg-light border-1 py-1 rounded-lg"
                                  v-model="safe_form_data[dq.model + '_file_comment']" rows="2"
                                  placeholder="Provide a reason..."></textarea>
                              </div>
                            </div>
                          </div>

                          <!-- RADIO -->
                          <div
                            v-else-if="dq.type === 'single_select' || dq.type === 'radio'"
                            class="radio-group mt-2 p-2 rounded"
                            :class="{ 'border border-danger': submit_attempted && validation_errors[dq.model] }"
                          >
                            <div
                              v-for="(option, opt_index) in dq.options || []"
                              :key="'dq-radio-' + opt_index"
                              class="form-check"
                            >
                              <input
                                class="form-check-input"
                                type="radio"
                                :name="dq.model"
                                :id="`${dq.model}-${opt_index}`"
                                v-model="safe_form_data[dq.model]"
                                :value="option.value || option.text"
                                :required="dq.required && dq.is_visible"
                              />
                              <label class="form-check-label text-xxs">
                                {{ option.text }}
                              </label>
                            </div>
                          </div>

                          <!-- CHECKBOX -->
                          <div
                            v-else-if="dq.type === 'multi_select' || dq.type === 'checkboxes'"
                            class="mt-2  p-2 rounded"
                            :class="{ 'border border-danger': submit_attempted && validation_errors[dq.model] }"
                          >
                            <div
                              v-for="(option, opt_index) in dq.options || []"
                              :key="'dq-checkbox-' + opt_index"
                              class="form-check"
                            >
                              <input
                                class="form-check-input"
                                type="checkbox"
                                :id="`${dq.model}-${opt_index}`"
                                :value="option.value || option.text"
                                v-model="safe_form_data[dq.model]"
                              />
                              <label class="form-check-label text-xxs">
                                {{ option.text }}
                              </label>
                            </div>
                          </div>

                          <!-- FALLBACK -->
                          <input
                            v-else
                            type="text"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            :class="{ 'border-danger': submit_attempted && validation_errors[dq.model] }"
                            :id="dq.model"
                            v-model="safe_form_data[dq.model]"
                            :placeholder="dq.label"
                            :required="dq.required && dq.is_visible"
                          />

                          <!-- ERROR MESSAGE -->
                          <div
                            v-if="submit_attempted && validation_errors[dq.model]"
                            class="text-danger text-xxs mt-1"
                          >
                            {{ validation_errors[dq.model] }}
                          </div>

                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="d-flex justify-content-center mt-4">
                   <button
      v-if="section_index !== safe_form_config.sections.length - 1"
      class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-lg text-dark fw-bold"
      @click="save_section"
    >
      Save Section
    </button>

    <button
      v-else
      class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-lg text-dark fw-bold"
      @click="submit_answers"
    >
      Submit
    </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>




  <div v-if="preview_modal" class="custom-modal-overlay" @click.self="preview_modal = false">
    <FilePreviewComponent :file="preview_url" @close="close_the_file_preview" :key="preview_url" />
  </div>

  <!-- Modal for file preview -->
   <teleport to="body">
  <div v-if="show_file_preview_modal" class="aic-modal-overlay" @click.self="close_file_preview_modal">
    <div class="modal-dialog aic-modal-dialog">  <!-- add aic-modal-dialog -->
      <div class="modal-content aic-modal-content bg-white rounded-3 p-0 shadow-lg" @click.stop> <!-- add aic-modal-content -->
        <div class="modal-header border-bottom-0 cute-header shadow-lg">
          <h5 class="modal-title text-white text-xxs">File Preview</h5>
          <button type="button" class="modal-btn-close btn-close" @click="close_file_preview_modal" aria-label="Close"></button>
        </div>

        <div class="modal-body aic-modal-body p-0"> <!-- add aic-modal-body -->
          <img
            v-if="preview_url && preview_type === 'image'"
            :src="preview_url"
            :alt="preview_name || 'Preview'"
          />
          <video
            v-else-if="preview_url && preview_type === 'video'"
            :src="preview_url"
            controls
          ></video>
          <iframe
            v-else-if="preview_url"
            :src="preview_url"
            frameborder="0"
          ></iframe>
        </div>
      </div>
    </div>
  </div>
  </teleport>

  <!-- Modal for request information -->
<teleport to="body">
  <div v-if="show_request_info_modal" class="modal show" @click="close_request_info_modal">
    <div class="modal show d-block" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <p class="modal-title text-white text-xxs">Request Information</p>
            <button type="button" class="modal-btn-close btn-close" @click="close_request_info_modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="answered_file_questions.length > 0" class="mb-3">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <p class="text-xxs fw-bold mb-0">Files in current section:</p>
              </div>
              <div class="mt-3">
                <div v-for="question in answered_file_questions" :key="question.section_question_id"
                  class="file-list-item">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-file-earmark me-2 text-primary"></i>
                      <div>
                        <div class="text-xxs">{{ question.question_text }}</div>
                      </div>
                    </div>
                    <input type="checkbox" :value="question.section_question_id"
                      :data-requested_field_text="question.question_text" class="form-check-input ms-3"
                      @change="toggle_requested_field($event, question)" />
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-form-group form-group mb-2 d-flex flex-column">
              <label for="request_info_note" class="modal-form-label form-label text-start text-xxs">Notes</label>
              <textarea v-model="request_info_note" id="request_info_note"
                class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm" rows="4"></textarea>
            </div>
          </div>
          <div class="modal-footer border-0 p-2 d-flex justify-content-end">
            <button type="button" class="btn btn-primary-dark btn-sm text-xxs shadow"
              @click="request_information">Request</button>
            <button type="button" class="btn btn-primary-dark btn-sm text-xxs ms-2"
              @click="close_request_info_modal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    </teleport>
  </div>
  </teleport>
</template>